import React, { useState } from 'react';
import { X, Search, Plus, CheckCircle, Check } from 'lucide-react';
import { interpolate } from '../../utils/localizedContent';

export default function CardSearchModal({
  show, onClose, t, cardType, setCardType,
  availableCardTypes = ['pokemon', 'magic', 'yugioh', 'onepiece', 'riftbound'],
  selectedCollectionTcg = 'mixed',
  cardSearch, setCardSearch, onSearch, searchLoading,
  searchResults, addedCards, onAddCard, onAddMultiple
}) {
  // Estado de selección múltiple
  const [multiSelected, setMultiSelected] = useState(new Map()); // external_id -> card

  if (!show) return null;

  const handleClose = () => {
    setMultiSelected(new Map());
    onClose();
  };

  const toggleSelect = (card) => {
    if (addedCards.has(card.external_id)) return; // ya está en la colección
    setMultiSelected(prev => {
      const next = new Map(prev);
      if (next.has(card.external_id)) {
        next.delete(card.external_id);
      } else {
        next.set(card.external_id, card);
      }
      return next;
    });
  };

  const handleAddSelected = () => {
    if (multiSelected.size === 0) return;
    const cards = Array.from(multiSelected.values());
    setMultiSelected(new Map());
    onAddMultiple(cards);
  };

  const selectedCount = multiSelected.size;

  const TCG_META = {
    pokemon: { label: 'Pokémon', color: '#D4A84B' },
    magic: { label: 'Magic', color: '#0D5E4A' },
    yugioh: { label: 'Yu-Gi-Oh!', color: '#7B3F00' },
    onepiece: { label: 'One Piece', color: '#C45B4A' },
    riftbound: { label: 'Riftbound', color: '#0BC4E2' },
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] flex flex-col" data-testid="card-search-modal">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#F5F0E6] font-bold" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('collections.search_cards')}
          </h3>
          <button onClick={handleClose} className="text-[#A8B5AE] hover:text-[#F5F0E6]">
            <X size={20} />
          </button>
        </div>

        {/* TCG Type Tabs + Search */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {availableCardTypes.length > 1 ? (
            <div className="flex bg-[#0A1612] rounded-xl p-1 gap-1 border border-[#C9A962]/20 flex-wrap">
              {availableCardTypes.map((type) => (
                <TCGTab
                  key={type}
                  type={type}
                  current={cardType}
                  onClick={setCardType}
                  color={TCG_META[type]?.color}
                  label={TCG_META[type]?.label || type}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center px-3 py-2 bg-[#0A1612] rounded-xl border border-[#C9A962]/20 text-[#A8B5AE] text-xs font-semibold">
              {selectedCollectionTcg === 'mixed'
                ? t('collectionsShared.mixed')
                : (TCG_META[selectedCollectionTcg]?.label || selectedCollectionTcg)}
            </div>
          )}
          <input
            value={cardSearch}
            onChange={e => setCardSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
            placeholder={t('collectionsShared.search_card_placeholder')}
            className="flex-1 min-w-[150px] bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-2 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] placeholder:text-[#A8B5AE]/50"
            data-testid="card-search-input"
          />
          <button onClick={onSearch} disabled={searchLoading} className="btn-primary px-4 py-2 text-sm" data-testid="search-cards-btn">
            <Search size={16} />
          </button>
        </div>

        {/* Selection hint */}
        {searchResults.length > 0 && (
          <p className="text-[#52525B] text-xs mb-2">
            {t('collectionsShared.multi_select_hint')}
          </p>
        )}

        {/* Results Grid */}
        <div className="overflow-y-auto flex-1">
          {searchLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#C9A962]/30 border-t-[#C9A962] rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {searchResults.map(card => {
                const isAdded = addedCards.has(card.external_id);
                const isSelected = multiSelected.has(card.external_id);
                return (
                  <button
                    key={card.external_id}
                    onClick={() => isAdded ? null : toggleSelect(card)}
                    disabled={isAdded}
                    className={`group text-left transition-transform ${
                      isAdded ? 'opacity-60 cursor-default' :
                      isSelected ? 'scale-95' : 'hover:scale-105'
                    }`}
                    data-testid={`search-result-${card.external_id}`}
                  >
                    <div className="aspect-[2/3] rounded-xl overflow-hidden bg-[#162B24] relative">
                      <img src={card.image_url || 'https://via.placeholder.com/150x210'} alt={card.name} className="w-full h-full object-cover" />
                      {isAdded && (
                        <div className="absolute inset-0 bg-[#10B981]/80 flex items-center justify-center">
                          <CheckCircle size={32} className="text-white" />
                        </div>
                      )}
                      {isSelected && !isAdded && (
                        <div className="absolute inset-0 bg-[#C9A962]/40 flex items-center justify-center border-2 border-[#C9A962] rounded-xl">
                          <div className="w-8 h-8 bg-[#C9A962] rounded-full flex items-center justify-center">
                            <Check size={18} className="text-[#0A1612]" strokeWidth={3} />
                          </div>
                        </div>
                      )}
                      {!isAdded && !isSelected && (
                        <div className="absolute inset-0 bg-[#C9A962]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Plus size={24} className="text-[#F5F0E6]" />
                        </div>
                      )}
                    </div>
                    <p className="text-[#F5F0E6] text-xs font-semibold mt-1 truncate">{card.name}</p>
                  </button>
                );
              })}
              {searchResults.length === 0 && !searchLoading && (
                <p className="col-span-5 text-center text-[#A8B5AE] text-sm py-8">
                  {t('collectionsShared.no_results')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Multi-select footer */}
        {selectedCount > 0 && (
          <div className="border-t border-[#C9A962]/20 pt-4 mt-3 flex items-center justify-between gap-3">
            <span className="text-[#C9A962] text-sm font-medium">
              {selectedCount} {selectedCount === 1
                ? t('collectionsShared.card_selected_singular')
                : t('collectionsShared.card_selected_plural')}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setMultiSelected(new Map())}
                className="px-3 py-1.5 bg-white/5 text-[#A1A1AA] rounded-lg text-sm hover:bg-white/10 transition-colors"
              >
                {t('collectionsShared.clear')}
              </button>
              <button
                onClick={handleAddSelected}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#C9A962] text-[#0A1612] rounded-lg text-sm font-semibold hover:bg-[#B8984E] transition-colors"
              >
                <Plus size={15} />
                {interpolate(t('collectionsShared.add_count'), { count: selectedCount })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TCGTab({ type, current, onClick, color, label }) {
  const isActive = current === type;
  return (
    <button
      onClick={() => onClick(type)}
      className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
        isActive ? 'text-[#0A1612]' : 'text-[#A8B5AE] hover:text-[#F5F0E6]'
      }`}
      style={isActive ? { backgroundColor: color } : {}}
      data-testid={`${type}-search-tab`}
    >
      {label}
    </button>
  );
}
